import { IS_BLOCK_EDITOR_MARKDOWN_ENABLED } from '@/components/constants/flagsWithUserQueryDep';
import { isEnableBlockEditorV1 } from '@/hooks/useEnableBlockEditorV1';
import { type StructuredNote } from '@/types';
import {
  stripEmptySectionHeadingPresentationHtml,
  stripPersistedEmptySectionHeadingLines,
  stripUnderlineHtmlPairs,
} from '@heidi/editor/core';
import { type Editor } from '@tiptap/react';
import { ClipboardItem } from 'clipboard-polyfill';
import { htmlToMarkdown, simpleHtmlToMarkdown } from '../htmlToMarkdown';
import { reformatHtml, simpleMarkdownToHtml } from '../markdownToHtml';
import {
  applyCopySettingsToLegacyEditorHtml,
  serializedClipboardWrite,
  writeHtmlAndMarkdownToClipboard,
} from './copyHelpers-v2';
import {
  oldCopyFullHtml,
  oldCopyHtmlWithoutLists,
  oldCopyPlainText,
} from './oldCopyHelper';
import { resolvePlainCopyMarkdown } from './plainTextClipboard';

const getPresentationEditorHtml = (editor: Editor | null): string =>
  stripEmptySectionHeadingPresentationHtml(editor?.getHTML() ?? '');

const getPresentationMarkdown = (markdown: string): string =>
  stripPersistedEmptySectionHeadingLines(markdown);

export const copyHtmlWithoutLists = async ({
  markdownContent,
  editor,
}: {
  markdownContent: string;
  editor: Editor | null;
}) => {
  const enableBlockEditorV1 = isEnableBlockEditorV1();
  const enableBlockEditorMarkdown = IS_BLOCK_EDITOR_MARKDOWN_ENABLED();
  if (!editor) {
    return oldCopyHtmlWithoutLists(getPresentationMarkdown(markdownContent));
  }
  if (!enableBlockEditorV1) {
    if (enableBlockEditorMarkdown) {
      return oldCopyHtmlWithoutLists(
        simpleHtmlToMarkdown({ html: getPresentationEditorHtml(editor), isCopy: true })
      );
    }
    return oldCopyHtmlWithoutLists(getPresentationMarkdown(markdownContent));
  }
  const htmlContent = getPresentationEditorHtml(editor);
  const listFreeHtml = applyCopySettingsToLegacyEditorHtml(htmlContent, {
    headings: true,
    boldAndItalics: true,
    bulletPoints: false,
    tables: true,
  });
  const listFreeMarkdown = htmlToMarkdown({ html: listFreeHtml });

  await writeHtmlAndMarkdownToClipboard({
    html: reformatHtml(listFreeHtml),
    markdown: listFreeMarkdown,
  });
};

export const copyFullHtml = async ({
  markdownContent,
  editor,
}: {
  markdownContent: string;
  editor: Editor | null;
}) => {
  const enableBlockEditorV1 = isEnableBlockEditorV1();
  const enableBlockEditorMarkdown = IS_BLOCK_EDITOR_MARKDOWN_ENABLED();
  if (!editor) {
    const presentationMarkdown = getPresentationMarkdown(markdownContent);
    return enableBlockEditorMarkdown
      ? oldCopyFullHtml({
          html: simpleMarkdownToHtml({ markdown: presentationMarkdown }),
        })
      : oldCopyFullHtml({ markdown: presentationMarkdown });
  }

  if (!enableBlockEditorV1) {
    if (enableBlockEditorMarkdown) {
      return oldCopyFullHtml({
        html: getPresentationEditorHtml(editor),
      });
    }
    return oldCopyFullHtml({
      markdown: getPresentationMarkdown(markdownContent),
    });
  }
  const htmlContent = getPresentationEditorHtml(editor);
  const markdown = htmlToMarkdown({ html: htmlContent });

  await writeHtmlAndMarkdownToClipboard({
    html: reformatHtml(htmlContent),
    markdown: resolvePlainCopyMarkdown(markdown),
  });
};

export const copyPlainText = async ({
  markdownContent,
  editor,
}: {
  markdownContent: string;
  editor: Editor | null;
}) => {
  const enableBlockEditorV1 = isEnableBlockEditorV1();
  const enableBlockEditorMarkdown = IS_BLOCK_EDITOR_MARKDOWN_ENABLED();
  if (!editor) {
    return oldCopyPlainText(getPresentationMarkdown(markdownContent));
  }

  if (!enableBlockEditorV1) {
    if (enableBlockEditorMarkdown) {
      return oldCopyPlainText(
        simpleHtmlToMarkdown({ html: getPresentationEditorHtml(editor), isCopy: true })
      );
    }
    return oldCopyPlainText(getPresentationMarkdown(markdownContent));
  }
  const markdown = htmlToMarkdown({ html: getPresentationEditorHtml(editor) });
  await writeHtmlAndMarkdownToClipboard({
    markdown: resolvePlainCopyMarkdown(markdown),
  });
};

export const getJsonNoteAsText = (jsonNote: StructuredNote): string => {
  if (!jsonNote?.clinical_note) {
    return '';
  }

  return Object.entries(jsonNote.clinical_note)
    .map(([key, value]) => `${key}:\n${value.content ?? ''}`)
    .join('\n\n');
};

export const copyJsonNote = async ({ jsonNote }: { jsonNote: StructuredNote }) => {
  const noteToCopy = getJsonNoteAsText(jsonNote);
  if (!noteToCopy) {
    return;
  }

  await serializedClipboardWrite([
    new ClipboardItem({
      'text/plain': new Blob([noteToCopy], { type: 'text/plain' }),
    }),
  ]);
};

export const copyJsonSection = async ({
  sectionKey,
  sectionEditor,
}: {
  sectionKey: string;
  sectionEditor: Editor | null;
}) => {
  const htmlContent = getPresentationEditorHtml(sectionEditor);
  const markdown = simpleHtmlToMarkdown({ html: htmlContent, isCopy: true });
  // This payload goes straight to text/plain (no strip funnel), so underline
  // pairs must be unwrapped here: the copy serializer now keeps `<u>` in the
  // markdown flavor for round-trip consumers, but a JSON-section copy has no
  // markdown consumer — the clinician pastes this into a plain EHR field.
  const sectionText = `${sectionKey}:\n${stripUnderlineHtmlPairs(markdown)}`;

  await serializedClipboardWrite([
    new ClipboardItem({
      'text/plain': new Blob([sectionText], { type: 'text/plain' }),
    }),
  ]);
};
